import { useCallback, useRef, useState } from 'react';

import { useToast } from '@/hooks/use-toast';
import type { ConnectionEvent, ConnectionState } from '@/lib/streamingClient';

/**
 * Tracks the streaming socket's connection state and tells the doctor when it
 * drops, comes back, or fails to rejoin — a failed rejoin otherwise loses the
 * recording silently. Pass `handleConnectionStateChange` to
 * `streamingClient.onConnectionStateChange()`.
 */
export const useConnectionStateToasts = () => {
  const { toast } = useToast();
  const [connectionState, setConnectionState] = useState<Exclude<ConnectionState, 'connecting'>>('connected');
  const dropToastShownRef = useRef(false);

  const handleConnectionStateChange = useCallback(
    (event: ConnectionEvent) => {
      if (event.state === 'reconnecting') {
        setConnectionState('reconnecting');
        if (!dropToastShownRef.current) {
          dropToastShownRef.current = true;
          toast({
            title: 'Tenging rofnaði',
            description: 'Reyni að endurtengja...',
            variant: 'destructive',
            duration: 8000,
          });
        }
      } else if (event.state === 'connected') {
        setConnectionState('connected');
        if (dropToastShownRef.current) {
          dropToastShownRef.current = false;
          const wasRejoined = event.reason === 'session_rejoined';
          toast({
            title: wasRejoined ? 'Endurtengt!' : 'Tenging komin á',
            description: wasRejoined ? 'Lota endurheimt - upptaka heldur áfram.' : 'Þú hefur náð tengingu á ný.',
            duration: 4000,
          });
        }
      } else if (event.state === 'disconnected') {
        setConnectionState('disconnected');
        dropToastShownRef.current = false;
        if (event.terminal) {
          // The session is over and will not come back, so this must not read as
          // a dropped connection the clinician should wait out. The tail warning
          // is deliberate: the server's final flush needs credentials it may no
          // longer have, so audio already sent may never become text.
          toast({
            title: 'Upptaka stöðvuð',
            description: event.tailMayBeIncomplete
              ? 'Lotan er útrunnin. Síðasti hluti upptökunnar gæti vantað. Byrjaðu nýja upptöku.'
              : 'Lotan er útrunnin. Byrjaðu nýja upptöku.',
            variant: 'destructive',
            duration: 0, // Don't auto-dismiss — they need to see this.
          });
          return;
        }
        if (event.reason?.includes('rejoin_failed')) {
          toast({
            title: 'Tenging glötuð',
            description: 'Endurtenging tókst ekki. Vinsamlegast endurræstu upptöku.',
            variant: 'destructive',
            duration: 0, // Don't auto-dismiss
          });
        }
      }
    },
    [toast]
  );

  return { connectionState, handleConnectionStateChange };
};
